/**
 * VAG Screen Repair — global.js
 *
 * The only script loaded on every page. Keep it tiny.
 * Responsibilities:
 *   1. Shared utilities on the `VSR` namespace (used by component scripts).
 *   2. Behaviour that must exist before any section renders.
 *
 * Everything else ships as a custom element in its own file, loaded by the
 * section that needs it. No frameworks, no jQuery, no polyfills.
 */

(function () {
  'use strict';

  /** Media query helpers mirroring the CSS breakpoints in base.css. */
  const BREAKPOINTS = Object.freeze({
    sm: 480,
    md: 750,
    lg: 990,
    xl: 1200,
    xxl: 1440
  });

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const mediaUp = (name) => window.matchMedia(`(min-width: ${BREAKPOINTS[name]}px)`);

  /**
   * Rate-limits a callback to one call per animation frame.
   * @param {Function} fn
   * @returns {Function}
   */
  function rafThrottle(fn) {
    let ticking = false;
    return function throttled(...args) {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        fn.apply(this, args);
      });
    };
  }

  /**
   * Debounces a callback.
   * @param {Function} fn
   * @param {number} wait
   * @returns {Function}
   */
  function debounce(fn, wait = 200) {
    let timer;
    return function debounced(...args) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /* Internal to trapFocus. Deliberately not exported: the one selector list
     the theme needs belongs to the one function that needs it, and an exported
     string invites a second, drifting copy of the same rule. */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])';

  /**
   * Traps focus inside a container until `release()` is called.
   * @param {HTMLElement} container
   * @returns {{release: Function}}
   */
  function trapFocus(container) {
    const previouslyFocused = document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);

    return {
      release() {
        container.removeEventListener('keydown', onKeydown);
        if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
      }
    };
  }

  /**
   * Locks body scroll without layout shift.
   *
   * The lock is a class, not an inline `overflow`: Shopify's cookie
   * preferences dialog sets and removes `body.style.overflow` itself, so the
   * two locks undid each other. See docs/css-notes/base.md#note-42.
   */
  const scrollLock = (() => {
    let locks = 0;
    return {
      lock() {
        if (locks === 0) {
          const scrollbar = window.innerWidth - document.documentElement.clientWidth;
          document.body.style.paddingInlineEnd = `${scrollbar}px`;
          document.body.classList.add('is-scroll-locked');
        }
        locks += 1;
      },
      unlock() {
        locks = Math.max(0, locks - 1);
        if (locks === 0) {
          document.body.style.paddingInlineEnd = '';
          document.body.classList.remove('is-scroll-locked');
        }
      }
    };
  })();

  /**
   * Registers a custom element once. Safe against Theme Editor re-injection.
   * @param {string} name
   * @param {CustomElementConstructor} ctor
   */
  function define(name, ctor) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  }

  // There is deliberately no `onSection` helper here. Custom elements already
  // get connectedCallback / disconnectedCallback on every Theme Editor render,
  // so the theme has no caller for a manual `shopify:section:load` fan-out —
  // it only installed a permanent document listener on every page for nobody.
  window.VSR = Object.freeze({
    BREAKPOINTS,
    prefersReducedMotion,
    mediaUp,
    rafThrottle,
    debounce,
    trapFocus,
    scrollLock,
    define
  });

  /* ---- Global behaviour --------------------------------------------------- */

  // Publish the header's real rendered height so scroll-padding, sticky
  // offsets and the mobile drawer line up with it.
  //
  // This deliberately writes to --vsr-header-offset, NOT --vsr-header-height.
  // --vsr-header-height is a *design knob*: it sets `min-height` on the header
  // bar. Measuring the rendered box and writing it back into that same token
  // created a feedback loop — the measurement includes the bottom border, so
  // every resize grew the header by another pixel, permanently. A measured
  // value must never be written to a token that layout consumes.
  const syncHeaderOffset = rafThrottle(() => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    document.documentElement.style.setProperty(
      '--vsr-header-offset',
      `${Math.round(header.getBoundingClientRect().height)}px`
    );
  });

  syncHeaderOffset();
  window.addEventListener('resize', debounce(syncHeaderOffset, 150), { passive: true });
  document.addEventListener('shopify:section:load', syncHeaderOffset);
})();
